// EDGE WALLS (owner 2026-11-02: "no longer have the invisible barrier… reach the edges of the map… like the rocks or the
// walls"). Shared builder for the solid, themed wall every map draws right at the arena edge. Render-only: the sim clamps the
// hero at ARENA_R; this draws the wall so its inner face sits just past that clamp (EDGE_FACE), so the hero visibly stops
// against it, not short of it.
//
// A map describes its wall as a few layers (main cliff / ruin wall, a taller back ridge, rubble or boulders at the foot,
// accents such as hoodoos, crystals or pylons). Each layer is one or two unit pieces (x across the wall in [-0.5, 0.5],
// y up in [0, 1], z outward from the inner face at z = 0), scattered around the circle with jittered spacing, size, yaw and a
// slow height wave so the skyline never repeats. Every piece is pushed out until no corner of its bounds pokes inside the
// face radius, so the wall never cuts into the walkable disc.
//
// Cheap: one InstancedMesh per piece part (+ its ink outline in cel mode), static matrices bucketed into 24 m chunks
// (render/chunkCull.ts) and refilled only when the set of chunks under the camera changes. Camera side: fragments standing
// between the camera and the ground around the hero fade to a see-through ghost (the canopy cutaway idea), so a wall on the
// near side never hides the fight. Prewarm: the meshes live in the env group, so render/prewarm.ts forceAll compiles them.
import * as THREE from 'three/webgpu';
import { SHADOW } from './camFrame';
import { mergeGeometries, mergeVertices } from 'three/addons/utils/BufferGeometryUtils.js';
import { cameraPosition, float, positionWorld, smoothstep } from 'three/tsl';
import { ARENA_R } from '../core/sim';
import { cutFocus } from './props';
import { easeShape, easeStep, GhostDepth, ghostBodyMaterial, ghostInkMaterial, ghostInkTwin, ghostTwin, setGhostCount, projectedBox } from './ghost';
import { ChunkGrid, CULL, GroundBox, viewBox } from './chunkCull';
import { outlineGeometry, outlineMaterial, toonMaterial } from './toon';
import type { EnvCtx } from './mapkit';

/** inner face of every wall (m from the centre): the hero's centre is clamped at ARENA_R and his body is ~0.45 m wide */
export const EDGE_FACE = ARENA_R + 0.5;
/** how far behind the face the wall body reaches (vista scenery closer than this would poke through the wall) */
export const EDGE_DEPTH = 9;

// ------------------------------------------------------------------------------------------------ noise helpers
const hash = (a: number, b: number): number => { const s = Math.sin(a * 127.1 + b * 311.7) * 43758.5453; return s - Math.floor(s); };
export const vnoise = (x: number, y: number): number => {
  const i = Math.floor(x), j = Math.floor(y), fx = x - i, fy = y - j, u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return (hash(i, j) * (1 - u) + hash(i + 1, j) * u) * (1 - v) + (hash(i, j + 1) * (1 - u) + hash(i + 1, j + 1) * u) * v;
};
const fbm = (x: number, y: number): number => vnoise(x, y) * 0.6 + vnoise(x * 2.1 + 5.3, y * 2.1 - 1.7) * 0.28 + vnoise(x * 4.3 - 2.2, y * 4.3 + 3.1) * 0.12;
/** small deterministic stream (mulberry32) */
export function edgeRng(seed: number): () => number {
  let a = (seed * 2654435761) >>> 0 || 1;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

// ------------------------------------------------------------------------------------------------ unit pieces
/** Fit a geometry to the unit piece box: x centred in [-0.5, 0.5], y from `sink` (below ground) to 1, z from 0 (inner face) to 1. */
export function unitize(g: THREE.BufferGeometry, sink = 0.04): THREE.BufferGeometry {
  g.computeBoundingBox();
  const b = g.boundingBox!, sx = b.max.x - b.min.x || 1, sy = b.max.y - b.min.y || 1, sz = b.max.z - b.min.z || 1;
  g.translate(-(b.min.x + b.max.x) / 2, -b.min.y, -b.min.z);
  g.scale(1 / sx, (1 + sink) / sy, 1 / sz);
  g.translate(0, -sink, 0);
  g.computeBoundingBox(); g.computeBoundingSphere();
  return g;
}

/** unitize several parts with ONE shared transform (e.g. a hoodoo body and its cap, a trunk and its crown) */
export function unitizeGroup(gs: THREE.BufferGeometry[], sink = 0.04): THREE.BufferGeometry[] {
  const b = new THREE.Box3();
  for (const g of gs) { g.computeBoundingBox(); b.union(g.boundingBox!); }
  const sx = b.max.x - b.min.x || 1, sy = b.max.y - b.min.y || 1, sz = b.max.z - b.min.z || 1;
  for (const g of gs) {
    g.translate(-(b.min.x + b.max.x) / 2, -b.min.y, -b.min.z); g.scale(1 / sx, (1 + sink) / sy, 1 / sz); g.translate(0, -sink, 0);
    g.computeBoundingBox(); g.computeBoundingSphere();
  }
  return gs;
}
/** lit material in the game's style (toon ramp when cel, else standard) coloured by a node */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function edgeLit(style: 'cel' | 'standard', colorNode: any, emissive?: any, rough = 0.95): THREE.NodeMaterial {
  const m = style === 'cel' ? toonMaterial() : new THREE.MeshStandardNodeMaterial({ roughness: rough });
  m.colorNode = colorNode; if (emissive) (m as THREE.MeshStandardNodeMaterial).emissiveNode = emissive;
  return m;
}

export interface CliffOpts {
  /** how far (share of the depth) the face recedes from foot to top: 0 = sheer, 0.8 = a dune-like slope */ lean?: number;
  /** top edge raggedness (share of the height) */ jag?: number;
  /** stepped ledges up the face (count) and their depth (share of the depth) */ ledges?: number; ledge?: number;
  /** vertical grooves / columns on the face (depth share) and how many across */ flute?: number; flutes?: number;
  /** lumpy face (depth share; always outward) */ bulge?: number;
  /** rounded shoulders at the piece ends (so neighbours blend) */ round?: number;
  segX?: number; segY?: number;
}
/** A cliff slab: displaced box with a receding, ledged, fluted face and a ragged crest. Inner face foot at z = 0. */
export function cliffPiece(seed: number, o: CliffOpts = {}): THREE.BufferGeometry {
  const lean = o.lean ?? 0.25, jag = o.jag ?? 0.25, ledges = o.ledges ?? 0, ledge = o.ledge ?? 0.06, flute = o.flute ?? 0.05, flutes = o.flutes ?? 7;
  const bulge = o.bulge ?? 0.08, round = o.round ?? 0.18;
  const g = new THREE.BoxGeometry(1, 1, 1, o.segX ?? 16, o.segY ?? 12, 3).translate(0, 0.5, 0.5);
  g.deleteAttribute('normal'); g.deleteAttribute('uv');
  const w = mergeVertices(g, 1e-4), pos = w.attributes.position as THREE.BufferAttribute, v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const x = v.x, front = 1 - v.z, ax = Math.abs(x);
    const crest = 1 - jag * fbm(x * 3.1 + seed * 1.37, seed * 0.71) - round * smoothstepJS(0.3, 0.5, ax);
    let y = v.y * Math.max(0.25, crest);
    let z = v.z + lean * Math.pow(v.y, 1.2) * front;                                         // face recedes with height
    if (ledges > 0) { const f = (v.y * ledges) % 1; z += ledge * f * front; }                // stepped strata
    z += flute * Math.abs(Math.sin(x * flutes * Math.PI + seed * 2.3 + vnoise(v.y * 2, seed) * 1.5)) * front;
    z += bulge * fbm(x * 4 + seed, v.y * 3.5 - seed) * front;
    z += round * 0.6 * smoothstepJS(0.3, 0.5, ax) * front;                                   // shoulders tuck back
    if (v.y < 1e-3) y = 0;
    v.set(x, y, z); pos.setXYZ(i, v.x, v.y, v.z);
  }
  w.computeVertexNormals();
  return unitize(w);
}
function smoothstepJS(a: number, b: number, x: number): number { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

/** A lumpy rock (high-poly blob with flattened bedding planes), not unitized. */
export function lumpRock(seed: number, detail = 2, flat = 0.35): THREE.BufferGeometry {
  const g = new THREE.IcosahedronGeometry(1, detail); g.deleteAttribute('normal'); g.deleteAttribute('uv');
  const w = mergeVertices(g, 1e-4), pos = w.attributes.position as THREE.BufferAttribute, v = new THREE.Vector3();
  for (let i = 0; i < pos.count; i++) {
    v.fromBufferAttribute(pos, i);
    const s = 0.8 + 0.3 * vnoise(v.x * 1.7 + seed, v.z * 1.7 + v.y * 1.3) + 0.08 * vnoise(v.x * 5 + seed * 2, v.y * 5 - v.z * 3);
    const yq = Math.round(v.y * 3) / 3; v.y = v.y * (1 - flat) + yq * flat;
    if (v.y < -0.4) v.y = -0.4 - (v.y + 0.4) * 0.2;
    v.multiplyScalar(s); pos.setXYZ(i, v.x, v.y, v.z);
  }
  w.computeVertexNormals(); return w;
}
/** A bank of n rocks heaped against the wall (big ones at the back, small in front). */
export function rubblePiece(seed: number, n = 5, detail = 2): THREE.BufferGeometry {
  const r = edgeRng(seed), parts: THREE.BufferGeometry[] = [];
  for (let k = 0; k < n; k++) {
    const back = k / Math.max(1, n - 1), rad = 0.55 - 0.3 * back * r() + 0.15 * r();
    const g = lumpRock(seed * 7 + k, detail).scale(rad * (1 + r() * 0.4), rad * (0.7 + r() * 0.5), rad);
    g.rotateY(r() * 6.28); g.translate((r() - 0.5) * 1.6, rad * 0.25, (1 - back) * 0.9 + r() * 0.3);
    parts.push(g.index ? g.toNonIndexed() : g);
  }
  const m = mergeGeometries(parts, false)!; m.computeVertexNormals();
  return unitize(m);
}
/** Pointed prism cluster (crystals, hoodoo fingers, pylons): n spikes leaning a little, the tallest in the middle. */
export function spikeCluster(seed: number, n = 4, sides = 6, tip = 0.25, lean = 0.25): THREE.BufferGeometry {
  const r = edgeRng(seed), parts: THREE.BufferGeometry[] = [];
  for (let k = 0; k < n; k++) {
    const main = k === 0, h = main ? 1 : 0.35 + r() * 0.5, rad = (main ? 0.16 : 0.07 + r() * 0.08);
    const g = new THREE.CylinderGeometry(rad * 0.75, rad, h * (1 - tip), sides, 1).translate(0, h * (1 - tip) / 2, 0);
    const c = new THREE.ConeGeometry(rad * 0.75, h * tip, sides).translate(0, h * (1 - tip) + h * tip / 2, 0);
    const s = mergeGeometries([g.toNonIndexed(), c.toNonIndexed()], false)!;
    s.rotateZ((r() - 0.5) * lean * 2); s.rotateX((r() - 0.3) * lean * 1.6); s.rotateY(r() * 6.28);
    s.translate(main ? 0 : (r() - 0.5) * 0.7, 0, main ? 0.25 : r() * 0.5);
    parts.push(s);
  }
  const m = mergeGeometries(parts, false)!; m.computeVertexNormals();
  return unitize(m, 0.02);
}
/** Ruined masonry: courses of jittered blocks with a broken, stepped top (stone walls, balustrades, rubble walls). */
export function blockWall(seed: number, courses = 5, perCourse = 6, broken = 0.5): THREE.BufferGeometry {
  const r = edgeRng(seed), parts: THREE.BufferGeometry[] = [];
  const top = (x: number) => 1 - broken * fbm(x * 2.2 + seed, seed * 0.3);
  for (let c = 0; c < courses; c++) {
    const y0 = c / courses, off = (c % 2) * 0.5 / perCourse;
    for (let k = -1; k < perCourse + 1; k++) {
      const x0 = -0.5 + (k + 0.5) / perCourse + off; if (x0 < -0.5 || x0 > 0.5) continue;
      if (y0 + 0.5 / courses > top(x0)) continue;
      const bw = (1 / perCourse) * (0.86 + r() * 0.1), bh = (1 / courses) * (0.84 + r() * 0.1), bd = 0.7 + r() * 0.3;
      const b = new THREE.BoxGeometry(bw, bh, bd).toNonIndexed();
      b.rotateY((r() - 0.5) * 0.12); b.rotateZ((r() - 0.5) * 0.06);
      b.translate(x0 + (r() - 0.5) * 0.02, y0 + bh / 2, bd / 2 + r() * 0.08);
      parts.push(b);
    }
  }
  const m = mergeGeometries(parts, false)!; m.computeVertexNormals();
  return unitize(m, 0.02);
}

// ------------------------------------------------------------------------------------------------ the wall
export interface EdgePart {
  geo: THREE.BufferGeometry;
  mat: THREE.Material;
  /** cel ink outline thickness (in the piece's local units after scale; 0 = none) */
  outline?: number;
}
export interface EdgeLayer {
  /** one instanced mesh per part, all sharing the layer's instance transforms (e.g. a hoodoo body + its cap) */
  parts: EdgePart[];
  /** arc length (m) between consecutive pieces */ spacing: [number, number];
  /** piece width along the wall, height, depth (m) */ w: [number, number]; h: [number, number]; d: [number, number];
  /** extra distance (m) of the inner face beyond EDGE_FACE (0 = the face the hero touches) and its jitter */ face?: number; faceJit?: number;
  /** yaw jitter (rad) around facing the centre; lean jitter (rad) */ yaw?: number; tilt?: number;
  /** slow height wave along the circle (0..1) so the skyline rises and dips */ wave?: number;
  /** chance a slot gets a piece (sparse accents) */ chance?: number;
  /** cast shadows (when the map has shadows) */ shadow?: boolean;
  /** camera-side cutaway (default on) */ cut?: boolean;
  /** random spin about the vertical axis instead of facing the centre (round pieces: rocks, trunks) */ spin?: boolean;
  /** sink below the ground (m) */ sink?: number;
  /** only place pieces at bearings (rad, atan2(z, x)) where this is true (themed sections, kept vistas) */
  where?: (a: number) => boolean;
}

const _m = new THREE.Matrix4(), _q = new THREE.Quaternion(), _e = new THREE.Euler(), _s = new THREE.Vector3(), _p = new THREE.Vector3(), _c = new THREE.Vector3();
const CORNERS: [number, number, number][] = [[-0.5, 0, 0], [0.5, 0, 0], [-0.5, 1, 0], [0.5, 1, 0], [-0.5, 0, 1], [0.5, 0, 1], [-0.5, 1, 1], [0.5, 1, 1], [0, 0, 0], [0, 1, 0]];

/** Wall cutaway, spatial part (per fragment, 0 = keep, 1 = fully cut): a wall fragment that hides arena floor (the view ray
 *  through it comes down inside the walkable disc) within ~8 m of the hero is cut, so the near / side wall never hides the
 *  fight. The far wall, whose rays come down outside the disc, is never touched. The foot (first ~1 m) stays solid so the
 *  edge still reads. The cut is drawn by ghost twins (ghost.ts: alpha blended down to EDGE_GHOST_MIN, eased in / out per
 *  piece, ink hulls fade out with the body); it used to be a screen-door dither, which a phone's low render scale turned
 *  into a coarse checkerboard (owner 2026-11-02). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function edgeCutS(): any {
  const F = cutFocus, cam = cameraPosition, P = positionWorld;
  const ray = P.sub(cam);
  const g = cam.add(ray.mul(F.y.sub(cam.y).div(ray.y.min(float(-0.001)))));
  const near = smoothstep(float(10.5), float(6.5), g.xz.sub(F.xz).length());
  const inside = smoothstep(float(ARENA_R + 1.2), float(ARENA_R - 0.3), g.xz.length());
  const above = float(1).sub(smoothstep(float(1.3), float(0.7), P.y.sub(F.y)));
  return near.mul(inside).mul(above);
}
/** alpha of a fully cut wall fragment (the dither kept ~30% of the pixels) */
const EDGE_GHOST_MIN = 0.3;
/** ghost twin capacity per wall layer (pieces beyond it simply stay solid) */
const EDGE_GHOST_CAP = 16;

/** a wall piece: layer, world matrix, and its world bounds for the CPU cut test (footprint centre x / z, radius, y range) */
interface Item { layer: number; m: Float32Array; cx: number; cz: number; r: number; y0: number; y1: number }
export interface EdgeWall {
  /** per frame (map env update): refill the instances from the chunks under the camera */
  update(): void;
  /** total pieces / pieces drawn last frame (QA) */
  readonly stats: { total: number; drawn: number };
}

/**
 * Build a map's edge wall into `group`. `seed` varies the layout per map. Returns the per-frame culling hook.
 */
export function buildEdgeWall(group: THREE.Group, ctx: EnvCtx, layers: EdgeLayer[], seed: number): EdgeWall {
  // QA: ?noedge leaves the wall out (perf A/B on the same build)
  if (typeof location !== 'undefined' && new URLSearchParams(location.search).has('noedge')) return { update: () => {}, stats: { total: 0, drawn: 0 } };
  const cel = ctx.style === 'cel', items: Item[] = [], R = EDGE_FACE;
  const counts = layers.map(() => 0);
  layers.forEach((L, li) => {
    const rnd = edgeRng(seed * 131 + li * 977 + 7);
    const circ = Math.PI * 2 * (R + (L.face ?? 0));
    let s = rnd() * L.spacing[1];
    const wPhase = rnd() * 100;
    while (s < circ) {
      const a = (s / circ) * Math.PI * 2;
      s += L.spacing[0] + rnd() * (L.spacing[1] - L.spacing[0]);
      const roll = rnd(), wv = rnd(), hv = rnd(), dv = rnd(), yv = rnd(), tv = rnd(), fv = rnd(), sv = rnd();
      if (L.chance !== undefined && roll > L.chance) continue;
      if (L.where && !L.where(Math.atan2(Math.sin(a), Math.cos(a)))) continue;
      const wave = 1 + (L.wave ?? 0) * (vnoise(a * 9 + wPhase, wPhase) * 2 - 1);
      const w = L.w[0] + wv * (L.w[1] - L.w[0]), h = (L.h[0] + hv * (L.h[1] - L.h[0])) * wave, d = L.d[0] + dv * (L.d[1] - L.d[0]);
      const dx = Math.cos(a), dz = Math.sin(a);
      // local +z (outward) along (dx, dz): yaw so the piece's +z axis maps to the radial direction
      const yaw = L.spin ? sv * Math.PI * 2 : Math.atan2(dx, dz) + (yv - 0.5) * 2 * (L.yaw ?? 0.12);
      _e.set((tv - 0.5) * 2 * (L.tilt ?? 0), yaw, (fv - 0.5) * 2 * (L.tilt ?? 0) * 0.5, 'YXZ'); _q.setFromEuler(_e);
      _s.set(w, h, d);
      let rad = R + (L.face ?? 0) + rnd() * (L.faceJit ?? 0);
      _p.set(dx * rad, -(L.sink ?? 0), dz * rad);
      _m.compose(_p, _q, _s);
      // push out until no corner of the piece's bounds pokes inside the face radius
      let minR = Infinity;
      for (const [cx, cy, cz] of CORNERS) { _c.set(cx, cy, cz).applyMatrix4(_m); minR = Math.min(minR, Math.hypot(_c.x, _c.z)); }
      const need = R + (L.face ?? 0) - minR;
      if (need > 0) { rad += need + 0.02; _p.set(dx * rad, -(L.sink ?? 0), dz * rad); _m.compose(_p, _q, _s); }
      // world bounds of the unit box (pieces may bulge a little past it: the cut test adds slack)
      let x0 = Infinity, x1 = -Infinity, y0 = Infinity, y1 = -Infinity, z0 = Infinity, z1 = -Infinity;
      for (const [cx, cy, cz] of CORNERS) { _c.set(cx, cy, cz).applyMatrix4(_m); x0 = Math.min(x0, _c.x); x1 = Math.max(x1, _c.x); y0 = Math.min(y0, _c.y); y1 = Math.max(y1, _c.y); z0 = Math.min(z0, _c.z); z1 = Math.max(z1, _c.z); }
      items.push({ layer: li, m: new Float32Array(_m.elements), cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, r: Math.hypot(x1 - x0, z1 - z0) / 2 + 0.5, y0, y1: y1 + 0.3 });
      counts[li]++;
    }
  });

  // meshes: one per layer part (+ outline twin), capacity = that layer's piece count
  // + per cut layer: ghost twins (ghost.ts) for the pieces near the hero, sharing one per-instance fade depth buffer
  const meshes: THREE.InstancedMesh[][] = [], outlines: THREE.InstancedMesh[][] = [];
  const ghosts: THREE.InstancedMesh[][] = [], ghostInk: THREE.InstancedMesh[][] = [], depths: (GhostDepth | null)[] = [];
  layers.forEach((L, li) => {
    const ms: THREE.InstancedMesh[] = [], os: THREE.InstancedMesh[] = [], gs: THREE.InstancedMesh[] = [], gi: THREE.InstancedMesh[] = [];
    const gcap = Math.min(Math.max(1, counts[li]), EDGE_GHOST_CAP), gd = L.cut !== false ? new GhostDepth(gcap) : null;
    for (const part of L.parts) {
      // the solid set uses the map's material as is (no mask any more: the cut is drawn by the ghost twins)
      const im = new THREE.InstancedMesh(part.geo, part.mat, Math.max(1, counts[li]));
      im.castShadow = !!(ctx.shadows && L.shadow !== false); im.receiveShadow = ctx.shadows;
      im.frustumCulled = false; im.name = 'edge-wall';
      group.add(im); ms.push(im);
      const g = gd ? ghostTwin(im, ghostBodyMaterial(part.mat, edgeCutS(), gd.node), gcap, false, ms.length - 1) : null;
      if (g) { group.add(g); gs.push(g); }
      if (cel && (part.outline ?? 0) > 0) {
        const og = outlineGeometry(part.geo);
        const o = new THREE.InstancedMesh(og, outlineMaterial(part.outline!), Math.max(1, counts[li]));
        o.instanceMatrix = im.instanceMatrix; o.frustumCulled = false; o.castShadow = false; o.receiveShadow = false;
        group.add(o); os.push(o);
        if (g && gd) { const k = ghostInkTwin(g, og, ghostInkMaterial(outlineMaterial(part.outline!), edgeCutS(), gd.node), gcap); group.add(k); gi.push(k); }
      }
    }
    meshes.push(ms); outlines.push(os); ghosts.push(gs); ghostInk.push(gi); depths.push(gd);
  });

  const grid = new ChunkGrid(ARENA_R + 30, 24);
  grid.build(items.length, (i) => items[i].m[12], (i) => items[i].m[14]);
  const box: GroundBox = { x0: 0, z0: 0, x1: 0, z1: 0 }, fill = layers.map(() => 0);
  const stats = { total: items.length, drawn: 0, ghosts: 0 };
  /** eased cut per piece (0..1), whether it is drawn by a ghost twin, the pieces in the visible chunks */
  const cutA = new Float32Array(items.length), ghosted = new Uint8Array(items.length), vis: number[] = [];
  let lastSig = -1, lastT = -1, nGhost = 0;
  const refill = (): void => {
    fill.fill(0);
    for (const i of vis) {
      if (ghosted[i]) continue;
      const it = items[i], n = fill[it.layer]++;
      for (const im of meshes[it.layer]) (im.instanceMatrix.array as Float32Array).set(it.m, n * 16);
    }
    let drawn = 0;
    layers.forEach((_, li) => {
      const n = fill[li]; drawn += n;
      for (const im of meshes[li]) { im.count = n; im.visible = n > 0; im.instanceMatrix.needsUpdate = true; }
      for (const o of outlines[li]) { o.count = n; o.visible = n > 0; }
    });
    stats.drawn = drawn;
  };
  const collect = (): void => {
    vis.length = 0;
    for (let v = 0; v < grid.visN; v++) { const c = grid.vis[v]; for (let q = grid.chunkStart(c), qe = grid.chunkEnd(c); q < qe; q++) vis.push(grid.item(q)); }
  };
  // start with everything drawn (prewarm compiles with whatever is there; the first update() culls)
  grid.select(null); collect(); refill();
  const hero = cutFocus.value as THREE.Vector3;
  const gb = new Float32Array(4), CUT_R = 10.5, DISC = ARENA_R + 1.2;
  /** could piece i have fragments in the cut (edgeCutS)? Generous; a miss only means no fade */
  const mayCut = (it: Item, cam: THREE.Vector3): boolean => {
    if (!projectedBox(cam, hero.y, it.cx, it.cz, it.r, Math.max(it.y0, hero.y + 0.7), it.y1, gb)) return false;
    if (gb[1] < hero.x - CUT_R || gb[0] > hero.x + CUT_R || gb[3] < hero.z - CUT_R || gb[2] > hero.z + CUT_R) return false;
    const nx = Math.max(gb[0], Math.min(0, gb[1])), nz = Math.max(gb[2], Math.min(0, gb[3])); // box point nearest the centre
    return nx * nx + nz * nz < DISC * DISC;
  };
  return {
    stats,
    update(): void {
      const cam = ctx.camera;
      const now = performance.now() / 1000, dt = lastT < 0 ? 0 : Math.min(0.1, Math.max(0, now - lastT)); lastT = now;
      grid.select(cam && !CULL.off ? viewBox(cam, CULL.margin + 10, box, hero.x, hero.z, ctx.shadows ? SHADOW.half + 32 : 0) : null);
      let sig = grid.visN;
      for (let v = 0; v < grid.visN; v++) sig = (sig * 31 + grid.vis[v] + 1) % 2147483647;
      let dirty = false;
      if (sig !== lastSig) { lastSig = sig; collect(); dirty = true; ghosted.fill(0); }
      // cutaway ghosts: only while the hero is near the wall (or a ghost is still fading out)
      const nearWall = !!cam && Math.hypot(hero.x, hero.z) > EDGE_FACE - 26;
      if (nearWall || nGhost > 0) {
        const per = layers.map(() => 0);
        nGhost = 0;
        for (const i of vis) {
          const it = items[i]; if (!depths[it.layer]) continue;
          const a = cutA[i] = easeStep(cutA[i], nearWall && mayCut(it, cam!.position), dt);
          const g = a > 0 && per[it.layer] < depths[it.layer]!.cap ? 1 : 0;
          if (g) { const k = per[it.layer]++; for (const m of ghosts[it.layer]) (m.instanceMatrix.array as Float32Array).set(it.m, k * 16); depths[it.layer]!.arr[k] = (1 - EDGE_GHOST_MIN) * easeShape(a); nGhost++; }
          if (g !== ghosted[i]) { ghosted[i] = g; dirty = true; }
        }
        layers.forEach((_, li) => {
          const n = per[li];
          for (const m of ghosts[li]) setGhostCount(m, n);
          for (const o of ghostInk[li]) { o.count = n; o.visible = n > 0; }
          if (n) depths[li]!.attr.needsUpdate = true;
        });
        stats.ghosts = nGhost;
      }
      if (dirty) refill();
    },
  };
}
